import _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { Module } from '../Module';
import { GetLocalAuthConfigurationRequest, ILocalAuthPolicyResult } from '../ServerApi/requests/GetLocalAuthConfigurationRequest';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { LocalAuthConfiguration } from '~src/authentication/models/LocalAuthConfiguration';
import { MaximumPasswordLengthPolicy } from '~src/authentication/models/validation/MaximumPasswordLengthPolicy';
import { IPasswordPolicy } from '~src/authentication/models/validation/IPasswordPolicy';
import { MustContainLettersPolicy } from '~src/authentication/models/validation/MustContainLettersPolicy';
import { MustContainUppercaseLettersPolicy } from '~src/authentication/models/validation/MustContainUppercaseLettersPolicy';
import { MustContainNumbersPolicy } from '~src/authentication/models/validation/MustContainNumbersPolicy';
import { MustContainSymbolsPolicy } from '~src/authentication/models/validation/MustContainSymbolsPolicy';
import { MinimumPasswordLengthPolicy } from '~src/authentication/models/validation/MinimumPasswordLengthPolicy';

export interface PasswordPolicyScope extends ng.IScope {
  passwordPolicyValidation: {
    shouldShowErrorMessages: () => boolean;
    mustContainLetters: () => boolean;
    mustContainUppercaseLetters: () => boolean;
    mustContainNumbers: () => boolean;
    mustContainSymbols: () => boolean;
    mustHaveMinimumPasswordLength: () => boolean;
    mustHaveMaximumPasswordLength: () => boolean;
    respectsAllPolicies: () => boolean;
    getMinLength: () => number;
    getMaxLength: () => number;
  };
}

/**
 * DON'T USE THIS.
 * 
 * @deprecated This class is deprecated and will be removed shortly as part of project arm.
 * Instead look at usages of LocalAuthConfiguration.
 */
export class PasswordPolicyService {
  // Enhances the scope to include the functionality introduced by the passwordPoliciesValidation mixin.
  // Receives the current scope, a getter for the password being introduced, and a callback to signal when the configuration
  // was correctly loaded from the server. Until that point, the validation would be turned off.
  // Returns a function that will provide the result of the validation applied to the new password accessor provided.
  public loadPoliciesIntoScope(scope: PasswordPolicyScope, getNewPassword: () => string,
    configurationLoadedCallback: () => void): () => boolean {

    // If the feature is disabled, we leave the scope as is and return that any password is valid.
    if (!FeatureFlags.passwordPoliciesEnabled) {
      configurationLoadedCallback();
      return () => true;
    }

    let currentConfig = LocalAuthConfiguration.default();
    let minimumPasswordLength = 0;
    let maximumPasswordLength = 0;

    // We start with an empty config and replace it when the server replies
    this.fetchConfiguration().then(function(serverConfig) {
      minimumPasswordLength = serverConfig.minimumPasswordLength;
      maximumPasswordLength = serverConfig.maximumPasswordLength;
      LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration().then((config) => {
        currentConfig = config;
        configurationLoadedCallback();
      });
    });

    function respectsPolicy(policy: IPasswordPolicy) {
      return currentConfig.performPasswordValidation(getNewPassword())
                          .filter(x => x.constructor.name === policy.constructor.name)
                          .length === 0;
    }

    scope.passwordPolicyValidation = {
      shouldShowErrorMessages: (): boolean => {
        return getNewPassword() && getNewPassword().length > 0;
      },
      respectsAllPolicies: (): boolean => {
        const newPassword = getNewPassword();
        return currentConfig.performPasswordValidation(newPassword).length === 0;
      },
      mustContainLetters: (): boolean => {
        return respectsPolicy(MustContainLettersPolicy.getPolicy());
      },
      mustContainUppercaseLetters: (): boolean => {
        return respectsPolicy(MustContainUppercaseLettersPolicy.getPolicy());
      },
      mustContainNumbers: (): boolean => {
        return respectsPolicy(MustContainNumbersPolicy.getPolicy());
      },
      mustContainSymbols: (): boolean => {
        return respectsPolicy(MustContainSymbolsPolicy.getPolicy());
      },
      mustHaveMinimumPasswordLength: (): boolean => {
        return respectsPolicy(new MinimumPasswordLengthPolicy(minimumPasswordLength));
      },
      mustHaveMaximumPasswordLength: (): boolean => {
        return respectsPolicy(new MaximumPasswordLengthPolicy(maximumPasswordLength));
      },
      getMinLength: (): number => {
        return minimumPasswordLength;
      },
      getMaxLength: (): number => {
        return maximumPasswordLength;
      },
    };
    return scope.passwordPolicyValidation.respectsAllPolicies;
  }

  private fetchConfiguration(): Promise<ILocalAuthPolicyResult> {
    return WebHttpClient.sendRequest(new GetLocalAuthConfigurationRequest()).then((result) => {
        return result;
    });
  }
}

Module.instance().service('PasswordPolicyService', PasswordPolicyService);
